import _ from '@luna/lodash';

const APP_CACHE_NAME = 'app';
const QUOTA_EXCEEDED_ERROR_CODE = 22;

interface Cache {
    get<T = unknown>(key: string): T | undefined;
    set<T>(key: string, value?: T): T | undefined;
    remove(key: string): void;
}

type CacheObjectType = Record<string, { value: unknown }>;

function isQuotaExceededError(value: unknown): boolean {
    if (!(value instanceof DOMException)) {
        return false;
    }

    if (
        value.name === 'QuotaExceededError' ||
        value.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        value.code === QUOTA_EXCEEDED_ERROR_CODE
    ) {
        return true;
    }

    return false;
}

function persistInAppCache(key: string, innerValue: CacheObjectType): undefined {
    const keys = Object.keys(innerValue);

    const copy = _.cloneDeep(innerValue);

    while (keys.length) {
        try {
            sessionStorage.setItem(APP_CACHE_NAME, JSON.stringify(copy));
            break;
        } catch (error) {
            if (!isQuotaExceededError(error)) {
                throw error;
            }

            const leastInsertedKey = keys.shift();

            if (leastInsertedKey) {
                delete copy[leastInsertedKey];
            }
        }
    }
}

function wrapValueAsCache(innerValue: CacheObjectType): Cache {
    return {
        get<T = unknown>(key: string): T | undefined {
            return innerValue[key]?.value as T | undefined;
        },
        set<T>(key: string, value?: T): T | undefined {
            innerValue[key] = { value };
            persistInAppCache(key, innerValue);

            return value;
        },
        remove(key: string) {
            delete innerValue[key];
            sessionStorage.setItem(APP_CACHE_NAME, JSON.stringify(innerValue));
        },
    };
}

const appCache: { current: Cache | undefined } = { current: undefined };

function setAppCache(value: Cache): Cache {
    appCache.current = value;
    return value;
}

function parseSessionStorageValue(sessionStorageValue?: string | null): CacheObjectType {
    if (sessionStorageValue) {
        const parsedValue = JSON.parse(sessionStorageValue);
        // there might be something wrong in sessionStorage
        if (!parsedValue || !(parsedValue instanceof Object)) {
            return {};
        }
        return parsedValue;
    }
    return {};
}

export function getAppCache(): Cache {
    if (appCache.current) {
        return appCache.current;
    }

    const sessionStorageAppCache = parseSessionStorageValue(sessionStorage.getItem(APP_CACHE_NAME));
    return setAppCache(wrapValueAsCache(sessionStorageAppCache));
}
